/** @jsx jsx */
import React from 'react';
import { jsx } from '@compiled/react';
import { EntryPointContainer, type EntryPoint, type EntryPointComponent } from 'react-relay';
import type { ErrorBoundaryFallbackComponent } from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { useHorizontalNavComponent } from '@atlassian/jira-navigation-apps-horizontal-nav-selector/src/controllers/nav-component-controller/index.tsx';
import { NavErrorBoundary } from '@atlassian/jira-horizontal-nav-error-boundary/src/ui/js-error-boundary/index.tsx';
import { usePageLayoutEntryPoint } from '@atlassian/jira-entry-point-page-layout/src/index.tsx';
import { HORIZONTAL_NAVIGATION } from '@atlassian/jira-entry-point-page-layout-fields/src/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { EntryPointReferencePlaceholder } from '@atlassian/jira-entry-point-placeholder/src/index.tsx';
import { HorizontalNavSkeleton } from '@atlassian/jira-horizontal-nav-common/src/ui/horizontal-nav-skeleton/index.tsx';
import { ReportSSRFailOnUse } from '@atlassian/jira-ssr-render-guard/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';

import { ProjectViewNavigationSkipLink } from '../page-container/page-container-skip-links/index.tsx';

const NavErrorBoundaryFallback: ErrorBoundaryFallbackComponent = () =>
	// null = equivalent of "unmount"
	// We should do more graceful degradation.
	// Let's review this after dogfooding:
	// Tracked https://hello.jira.atlassian.cloud/browse/SEA-3059
	null;

const entrypointContainerProps = {};

export const HorizontalNav = () => {
	const HorizontalNavComponent = useHorizontalNavComponent();
	// Consume the horizontal nav entry point if defined, otherwise fallback to the old horizontal nav component
	const horizontalNavEntryPointReference =
		usePageLayoutEntryPoint<
			EntryPoint<
				EntryPointComponent<{}, {}, {}, { fallback: () => JSX.Element | undefined }>,
				{ fallback: () => JSX.Element | undefined }
			>
		>(HORIZONTAL_NAVIGATION);

	const horizontalNavFallback = () => {
		const Fallback =
			horizontalNavEntryPointReference?.extraProps?.fallback || HorizontalNavSkeleton;
		if (fg('blu-8405-report-ssr-on-fail-tab-nav'))
			return (
				<>
					<ReportSSRFailOnUse
						pageId="horizontal-nav-through-entry-point"
						fallbackId="horizontal-nav-through-entry-point"
						displayErrorInStaging
					/>
					<Fallback />
				</>
			);
		return <Fallback />;
	};

	if (horizontalNavEntryPointReference) {
		return (
			<>
				<NavErrorBoundary
					packageName="page-container"
					id="projectNavigationSkipLink"
					fallback="unmount"
				>
					<ProjectViewNavigationSkipLink />
				</NavErrorBoundary>
				<UFOSegment name="horizontal-nav-through-entry-point">
					<JSErrorBoundary
						fallback="unmount"
						id="horizontal-nav-through-entry-point"
						packageName="page-container"
					>
						<EntryPointReferencePlaceholder
							name="horizontal-nav-through-entry-point"
							fallback={horizontalNavFallback()}
							entryPointReference={horizontalNavEntryPointReference}
						>
							<EntryPointContainer
								entryPointReference={horizontalNavEntryPointReference}
								props={entrypointContainerProps}
							/>
						</EntryPointReferencePlaceholder>
					</JSErrorBoundary>
				</UFOSegment>
			</>
		);
	}

	return (
		HorizontalNavComponent && (
			<NavErrorBoundary
				packageName="page-container"
				id="spa-horizontal-nav"
				fallback={NavErrorBoundaryFallback}
			>
				<NavErrorBoundary
					packageName="page-container"
					id="projectNavigationSkipLink"
					fallback="unmount"
				>
					<ProjectViewNavigationSkipLink />
				</NavErrorBoundary>
				<HorizontalNavComponent />
			</NavErrorBoundary>
		)
	);
};
